import secureApi from '../../utils/secureApi';
import { getTournamentTeamRegistrations, TournamentTeamRegistration } from '../tournament/registeredTournamentsService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// =========================
// Fixtures Interfaces
// =========================

export interface AgeCategoryFixture {
  _id: string;
  ageCategory: string;
  startDate: string;
  tournamentType: 'Group + Knockout' | 'Direct Knockout';
  status: 'Upcoming' | 'Ongoing' | 'Completed';
  tournamentId: string;
}

export interface Fixture {
  _id: string;
  tournamentName: string;
  sports: string;
  ageCategory: string;
  startDate: string;
  endDate: string;
  venue: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  tournamentId?: string;
  fixtureType?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

// API response interface
export interface FixturesApiResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    paginatedResults: Fixture[];
    totalCount: number;
  };
}

// Single fixture API response
export interface FixtureApiResponse {
  statusCode: number;
  status: string;
  message: string;
  data: Fixture;
}

// =========================
// Dummy Data - Tournaments Available for Fixtures
// =========================

// Interface for tournaments available for fixtures (using ManageTournament structure)
export interface TournamentForFixture {
  _id: string;
  name: string;
  shortName: string;
  sports: string;
  startDate: string;
  endDate: string;
  location: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  ageCategories: string[];
  totalTeams: number;
  registeredTeams: number;
  logo?: string;
  thumbnail?: string;
  registrationStartDate?: string;
  registrationEndDate?: string;
  isActive?: boolean;
  isRegistrationOpen?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const dummyTournamentsForFixtures: TournamentForFixture[] = [
  {
    _id: '1',
    name: 'SFL Football League Pune Edition',
    shortName: 'SFL Pune',
    sports: 'Football',
    startDate: '2025-10-04',
    endDate: '2025-11-15',
    location: 'Pune',
    status: 'upcoming',
    ageCategories: ['U8(Co-Ed)', 'U10(Co-Ed)', 'U12(Co-Ed)', 'Sub Junior Boys', 'Junior Boys', 'Elite Boys', 'U14 Girls', 'U17 Girls'],
    totalTeams: 32,
    registeredTeams: 24,
    logo: '',
    thumbnail: '',
    registrationStartDate: '2025-08-01',
    registrationEndDate: '2025-09-15',
    isActive: true,
    isRegistrationOpen: true,
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2024-09-01T10:00:00Z'
  },
  {
    _id: '2',
    name: 'SFL Football League Mumbai Edition',
    shortName: 'SFL Mumbai',
    sports: 'Football',
    startDate: '2025-09-27',
    endDate: '2025-10-05',
    location: 'Mumbai',
    status: 'upcoming',
    ageCategories: ['U8(Co-Ed)', 'U10(Co-Ed)', 'U12(Co-Ed)', 'Sub Junior Boys', 'Junior Boys', 'Elite Boys', 'U14 Girls', 'U17 Girls'],
    totalTeams: 28,
    registeredTeams: 22,
    logo: '',
    thumbnail: '',
    registrationStartDate: '2025-07-15',
    registrationEndDate: '2025-09-10',
    isActive: true,
    isRegistrationOpen: true,
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2024-09-01T10:00:00Z'
  }
];

const dummyFixtures: Fixture[] = [
  {
    _id: '1',
    tournamentName: 'SFL Football League Mumbai Edition',
    sports: 'Football',
    ageCategory: 'U8(Co-Ed)',
    startDate: '2024-09-27',
    endDate: '2024-10-05',
    venue: 'Mumbai',
    status: 'upcoming',
    tournamentId: 'tournament_1',
    fixtureType: 'League',
    description: 'Mumbai edition of SFL Football League for U8 category',
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2024-09-01T10:00:00Z'
  },
  {
    _id: '2',
    tournamentName: 'SFL Football League Mumbai Edition',
    sports: 'Football',
    ageCategory: 'U8(Co-Ed)',
    startDate: '2024-09-27',
    endDate: '2024-10-05',
    venue: 'Mumbai',
    status: 'upcoming',
    tournamentId: 'tournament_1',
    fixtureType: 'League',
    description: 'Mumbai edition of SFL Football League for U8 category',
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2024-09-01T10:00:00Z'
  },
  {
    _id: '3',
    tournamentName: 'SFL Tennis Championship Pune',
    sports: 'Tennis',
    ageCategory: 'U12',
    startDate: '2024-10-15',
    endDate: '2024-10-20',
    venue: 'Pune',
    status: 'upcoming',
    tournamentId: 'tournament_2',
    fixtureType: 'Championship',
    description: 'Pune Tennis Championship for U12 category',
    createdAt: '2024-09-05T14:30:00Z',
    updatedAt: '2024-09-05T14:30:00Z'
  },
  {
    _id: '4',
    tournamentName: 'SFL Pickleball Tournament Bengaluru',
    sports: 'Pickleball',
    ageCategory: 'U14',
    startDate: '2024-10-01',
    endDate: '2024-10-03',
    venue: 'Bengaluru',
    status: 'ongoing',
    tournamentId: 'tournament_3',
    fixtureType: 'Tournament',
    description: 'Bengaluru Pickleball Tournament for U14 category',
    createdAt: '2024-09-10T09:15:00Z',
    updatedAt: '2024-09-10T09:15:00Z'
  },
  {
    _id: '5',
    tournamentName: 'SFL Football League Delhi Edition',
    sports: 'Football',
    ageCategory: 'U16',
    startDate: '2024-08-15',
    endDate: '2024-08-25',
    venue: 'Delhi',
    status: 'completed',
    tournamentId: 'tournament_4',
    fixtureType: 'League',
    description: 'Delhi edition of SFL Football League for U16 category',
    createdAt: '2024-08-01T11:00:00Z',
    updatedAt: '2024-08-25T18:00:00Z'
  },
  {
    _id: '6',
    tournamentName: 'SFL Cricket Championship Chennai',
    sports: 'Cricket',
    ageCategory: 'U18',
    startDate: '2024-11-01',
    endDate: '2024-11-10',
    venue: 'Chennai',
    status: 'upcoming',
    tournamentId: 'tournament_5',
    fixtureType: 'Championship',
    description: 'Chennai Cricket Championship for U18 category',
    createdAt: '2024-09-15T16:45:00Z',
    updatedAt: '2024-09-15T16:45:00Z'
  }
];

// =========================
// Helper Functions
// =========================

/**
 * Simulate API delay for realistic behavior
 */
const simulateApiDelay = (ms: number = 500): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Generate mock API response structure
 */
const createMockApiResponse = (data: Fixture[], totalCount?: number): FixturesApiResponse => {
  return {
    statusCode: 200,
    status: 'success',
    message: 'Fixtures retrieved successfully',
    data: {
      paginatedResults: data,
      totalCount: totalCount || data.length
    }
  };
};

/**
 * Generate mock single fixture API response
 */
const createMockSingleFixtureResponse = (data: Fixture): FixtureApiResponse => {
  return {
    statusCode: 200,
    status: 'success',
    message: 'Fixture retrieved successfully',
    data: data
  };
};

// =========================
// Fixtures APIs (Dummy Data + API-Ready)
// =========================

// =========================
// API Functions for Tournaments Available for Fixtures
// =========================

/**
 * Get tournaments available for creating fixtures
 * @param page - Page number for pagination (default: 1)
 * @param limit - Number of items per page (default: 10)
 * @param search - Search term for filtering tournaments
 * @param status - Status filter for tournaments
 * @param sports - Sports filter for tournaments
 * @returns Promise with tournaments data and pagination info
 */
export const getTournamentsForFixtures = async (
  page: number = 1,
  limit: number = 10,
  search: string = '',
  status: string = 'upcoming',
  sports: string = ''
): Promise<{ tournaments: TournamentForFixture[]; totalCount: number; totalPages: number }> => {
  try {
    // Use the tournament registration API
    const response = await getTournamentTeamRegistrations(page);
    
    // Check if we got any tournaments
    if (!response.data || !response.data.paginatedResults || response.data.paginatedResults.length === 0) {
      throw new Error('No tournaments found from API');
    }
    
    // Convert TournamentTeamRegistration to TournamentForFixture format
    const tournaments: TournamentForFixture[] = response.data.paginatedResults.map((tournament: TournamentTeamRegistration) => ({
      _id: tournament._id,
      name: tournament.name,
      shortName: tournament.shortName,
      sports: tournament.sports,
      startDate: tournament.startDate,
      endDate: tournament.endDate,
      location: tournament.location,
      status: tournament.status as 'upcoming' | 'ongoing' | 'completed' | 'cancelled',
      ageCategories: tournament.ageCategories,
      totalTeams: 0, // This would come from team registration data
      registeredTeams: 0, // This would come from team registration data
      logo: tournament.logo,
      thumbnail: tournament.thumbnail,
      registrationStartDate: tournament.registrationStartDate,
      registrationEndDate: tournament.registrationEndDate,
      isActive: true, // Default to true for registered tournaments
      isRegistrationOpen: true, // Default to true for registered tournaments
      createdAt: '',
      updatedAt: ''
    }));

    // Apply client-side filters for search and sports (since API doesn't support these)
    let filteredTournaments = tournaments;

    if (search) {
      filteredTournaments = filteredTournaments.filter(tournament =>
        tournament.name.toLowerCase().includes(search.toLowerCase()) ||
        tournament.shortName.toLowerCase().includes(search.toLowerCase()) ||
        tournament.sports.toLowerCase().includes(search.toLowerCase()) ||
        tournament.location.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (sports) {
      filteredTournaments = filteredTournaments.filter(tournament => tournament.sports === sports);
    }

    // Calculate pagination for filtered results
    const totalCount = response.data.totalCount || filteredTournaments.length;
    const totalPages = Math.ceil(totalCount / limit);

    return {
      tournaments: filteredTournaments,
      totalCount,
      totalPages
    };
  } catch (error) {
    console.error('Error fetching tournaments for fixtures:', error);
    
    // Fallback to dummy data if API fails
    let filteredTournaments = [...dummyTournamentsForFixtures];

    // Apply search filter
    if (search) {
      filteredTournaments = filteredTournaments.filter(tournament =>
        tournament.name.toLowerCase().includes(search.toLowerCase()) ||
        tournament.shortName.toLowerCase().includes(search.toLowerCase()) ||
        tournament.sports.toLowerCase().includes(search.toLowerCase()) ||
        tournament.location.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Apply status filter
    if (status) {
      filteredTournaments = filteredTournaments.filter(tournament => tournament.status === status);
    }

    // Apply sports filter
    if (sports) {
      filteredTournaments = filteredTournaments.filter(tournament => tournament.sports === sports);
    }

    // Calculate pagination
    const totalCount = filteredTournaments.length;
    const totalPages = Math.ceil(totalCount / limit);
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedTournaments = filteredTournaments.slice(startIndex, endIndex);

    return {
      tournaments: paginatedTournaments,
      totalCount,
      totalPages
    };
  }
};

/**
 * Search tournaments for fixtures
 * @param searchTerm - Search term for filtering tournaments
 * @param page - Page number for pagination (default: 1)
 * @param limit - Number of items per page (default: 10)
 * @returns Promise with filtered tournaments data and pagination info
 */
export const searchTournamentsForFixtures = async (
  searchTerm: string,
  page: number = 1,
  limit: number = 10
): Promise<{ tournaments: TournamentForFixture[]; totalCount: number; totalPages: number }> => {
  return getTournamentsForFixtures(page, limit, searchTerm);
};

// =========================
// Age Category Fixtures API
// =========================

/**
 * Get age category fixtures for a tournament
 * @param tournamentId - The tournament ID
 * @returns Promise<AgeCategoryFixture[]>
 */
export const getAgeCategoryFixtures = async (tournamentId: string): Promise<AgeCategoryFixture[]> => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // For now, return dummy data
    // TODO: Replace with actual API call when backend is ready
    const dummyFixtures: AgeCategoryFixture[] = [
      {
        _id: `${tournamentId}-1`,
        ageCategory: 'U8(Co-Ed)',
        startDate: '27/09/2025',
        tournamentType: 'Direct Knockout',
        status: 'Upcoming',
        tournamentId
      },
      {
        _id: `${tournamentId}-2`,
        ageCategory: 'U10(Co-Ed)',
        startDate: '27/09/2025',
        tournamentType: 'Group + Knockout',
        status: 'Ongoing',
        tournamentId
      }
    ];
    
    return dummyFixtures;
  } catch (error) {
    console.error('Error fetching age category fixtures:', error);
    throw new Error('Failed to fetch age category fixtures');
  }
};

/**
 * Create age category fixture
 * @param fixtureData - The fixture data to create
 * @returns Promise<AgeCategoryFixture>
 */
export const createAgeCategoryFixture = async (fixtureData: Omit<AgeCategoryFixture, '_id'>): Promise<AgeCategoryFixture> => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // For now, return dummy data
    // TODO: Replace with actual API call when backend is ready
    const newFixture: AgeCategoryFixture = {
      _id: `${fixtureData.tournamentId}-${Date.now()}`,
      ...fixtureData
    };
    
    return newFixture;
  } catch (error) {
    console.error('Error creating age category fixture:', error);
    throw new Error('Failed to create age category fixture');
  }
};

/**
 * Update age category fixture
 * @param fixtureId - The fixture ID
 * @param fixtureData - The updated fixture data
 * @returns Promise<AgeCategoryFixture>
 */
export const updateAgeCategoryFixture = async (fixtureId: string, fixtureData: Partial<AgeCategoryFixture>): Promise<AgeCategoryFixture> => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // For now, return dummy data
    // TODO: Replace with actual API call when backend is ready
    const updatedFixture: AgeCategoryFixture = {
      _id: fixtureId,
      ageCategory: fixtureData.ageCategory || '',
      startDate: fixtureData.startDate || '',
      tournamentType: fixtureData.tournamentType || 'Group + Knockout',
      status: fixtureData.status || 'Upcoming',
      tournamentId: fixtureData.tournamentId || ''
    };
    
    return updatedFixture;
  } catch (error) {
    console.error('Error updating age category fixture:', error);
    throw new Error('Failed to update age category fixture');
  }
};

// =========================
// API Functions for Fixtures
// =========================

/**
 * Fetch fixtures with pagination
 * @param pageNum - Page number for pagination (default: 1)
 * @param pageSize - Number of items per page (default: 10)
 * @returns Promise<FixturesApiResponse>
 */
export const getFixtures = async (pageNum: number = 1, pageSize: number = 10): Promise<FixturesApiResponse> => {
  try {
    // Simulate API delay
    await simulateApiDelay();
    
    // For now, use dummy data
    // TODO: Replace with actual API call when backend is ready
    if (process.env.NODE_ENV === 'development' || !API_BASE_URL) {
      console.log('🔧 Using dummy fixtures data');
      
      // Simulate pagination
      const startIndex = (pageNum - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = dummyFixtures.slice(startIndex, endIndex);
      
      return createMockApiResponse(paginatedData, dummyFixtures.length);
    }
    
    // TODO: Uncomment when API is ready
    /*
    const response = await secureApi.get<FixturesApiResponse>(
      `/api/v1/fixtures`,
      {
        params: { pageNum, pageSize }
      }
    );
    
    return response.data;
    */
    
    // Fallback to dummy data
    return createMockApiResponse(dummyFixtures, dummyFixtures.length);
    
  } catch (error: any) {
    console.error('❌ Error fetching fixtures:', error);
    
    // Return dummy data as fallback
    console.log('🔧 Falling back to dummy fixtures data');
    return createMockApiResponse(dummyFixtures, dummyFixtures.length);
  }
};

/**
 * Get fixture by ID
 * @param fixtureId - The fixture ID
 * @returns Promise<Fixture>
 */
export const getFixtureById = async (fixtureId: string): Promise<Fixture> => {
  try {
    // Simulate API delay
    await simulateApiDelay();
    
    // For now, use dummy data
    if (process.env.NODE_ENV === 'development' || !API_BASE_URL) {
      console.log('🔧 Using dummy fixture data for ID:', fixtureId);
      
      const fixture = dummyFixtures.find(f => f._id === fixtureId);
      if (fixture) {
        return fixture;
      }
      throw new Error('Fixture not found');
    }
    
    // TODO: Uncomment when API is ready
    /*
    const response = await secureApi.get<FixtureApiResponse>(
      `/api/v1/fixtures/${fixtureId}`
    );
    
    return response.data.data;
    */
    
    // Fallback to dummy data
    const fixture = dummyFixtures.find(f => f._id === fixtureId);
    if (fixture) {
      return fixture;
    }
    throw new Error('Fixture not found');
    
  } catch (error: any) {
    console.error('❌ Error fetching fixture by ID:', error);
    throw error;
  }
};

/**
 * Add new fixture
 * @param fixtureData - The fixture data to add
 * @returns Promise<Fixture>
 */
export const addFixture = async (fixtureData: Omit<Fixture, '_id' | 'createdAt' | 'updatedAt'>): Promise<Fixture> => {
  try {
    // Simulate API delay
    await simulateApiDelay();
    
    // For now, use dummy data
    if (process.env.NODE_ENV === 'development' || !API_BASE_URL) {
      console.log('🔧 Adding dummy fixture:', fixtureData);
      
      const newFixture: Fixture = {
        ...fixtureData,
        _id: `fixture_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Add to dummy data
      dummyFixtures.unshift(newFixture);
      
      return newFixture;
    }
    
    // TODO: Uncomment when API is ready
    /*
    const response = await secureApi.post<FixtureApiResponse>(
      `/api/v1/fixtures`,
      fixtureData
    );
    
    return response.data.data;
    */
    
    // Fallback to dummy data
    const newFixture: Fixture = {
      ...fixtureData,
      _id: `fixture_${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    dummyFixtures.unshift(newFixture);
    return newFixture;
    
  } catch (error: any) {
    console.error('❌ Error adding fixture:', error);
    throw error;
  }
};

/**
 * Update existing fixture
 * @param fixtureId - The fixture ID to update
 * @param fixtureData - The updated fixture data
 * @returns Promise<Fixture>
 */
export const updateFixture = async (fixtureId: string, fixtureData: Partial<Fixture>): Promise<Fixture> => {
  try {
    // Simulate API delay
    await simulateApiDelay();
    
    // For now, use dummy data
    if (process.env.NODE_ENV === 'development' || !API_BASE_URL) {
      console.log('🔧 Updating dummy fixture:', fixtureId, fixtureData);
      
      const fixtureIndex = dummyFixtures.findIndex(f => f._id === fixtureId);
      if (fixtureIndex === -1) {
        throw new Error('Fixture not found');
      }
      
      const updatedFixture: Fixture = {
        ...dummyFixtures[fixtureIndex],
        ...fixtureData,
        _id: fixtureId,
        updatedAt: new Date().toISOString()
      };
      
      dummyFixtures[fixtureIndex] = updatedFixture;
      return updatedFixture;
    }
    
    // TODO: Uncomment when API is ready
    /*
    const response = await secureApi.put<FixtureApiResponse>(
      `/api/v1/fixtures/${fixtureId}`,
      fixtureData
    );
    
    return response.data.data;
    */
    
    // Fallback to dummy data
    const fixtureIndex = dummyFixtures.findIndex(f => f._id === fixtureId);
    if (fixtureIndex === -1) {
      throw new Error('Fixture not found');
    }
    
    const updatedFixture: Fixture = {
      ...dummyFixtures[fixtureIndex],
      ...fixtureData,
      _id: fixtureId,
      updatedAt: new Date().toISOString()
    };
    
    dummyFixtures[fixtureIndex] = updatedFixture;
    return updatedFixture;
    
  } catch (error: any) {
    console.error('❌ Error updating fixture:', error);
    throw error;
  }
};

/**
 * Delete fixture
 * @param fixtureId - The fixture ID to delete
 * @returns Promise<void>
 */
export const deleteFixture = async (fixtureId: string): Promise<void> => {
  try {
    // Simulate API delay
    await simulateApiDelay();
    
    // For now, use dummy data
    if (process.env.NODE_ENV === 'development' || !API_BASE_URL) {
      console.log('🔧 Deleting dummy fixture:', fixtureId);
      
      const fixtureIndex = dummyFixtures.findIndex(f => f._id === fixtureId);
      if (fixtureIndex === -1) {
        throw new Error('Fixture not found');
      }
      
      dummyFixtures.splice(fixtureIndex, 1);
      return;
    }
    
    // TODO: Uncomment when API is ready
    /*
    await secureApi.delete(`/api/v1/fixtures/${fixtureId}`);
    return;
    */
    
    // Fallback to dummy data
    const fixtureIndex = dummyFixtures.findIndex(f => f._id === fixtureId);
    if (fixtureIndex === -1) {
      throw new Error('Fixture not found');
    }
    
    dummyFixtures.splice(fixtureIndex, 1);
    
  } catch (error: any) {
    console.error('❌ Error deleting fixture:', error);
    throw error;
  }
};

/**
 * Search fixtures
 * @param searchTerm - Search term
 * @param filters - Additional filters
 * @returns Promise<FixturesApiResponse>
 */
export const searchFixtures = async (
  searchTerm: string = '',
  filters: {
    sports?: string;
    venue?: string;
    ageCategory?: string;
    status?: string;
  } = {}
): Promise<FixturesApiResponse> => {
  try {
    // Simulate API delay
    await simulateApiDelay();
    
    // For now, use dummy data
    if (process.env.NODE_ENV === 'development' || !API_BASE_URL) {
      console.log('🔧 Searching dummy fixtures:', { searchTerm, filters });
      
      let filteredFixtures = [...dummyFixtures];
      
      // Apply search term
      if (searchTerm) {
        filteredFixtures = filteredFixtures.filter(fixture =>
          fixture.tournamentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          fixture.venue.toLowerCase().includes(searchTerm.toLowerCase()) ||
          fixture.sports.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Apply filters
      if (filters.sports && filters.sports !== 'all') {
        filteredFixtures = filteredFixtures.filter(fixture => fixture.sports === filters.sports);
      }
      
      if (filters.venue && filters.venue !== 'all') {
        filteredFixtures = filteredFixtures.filter(fixture => fixture.venue === filters.venue);
      }
      
      if (filters.ageCategory && filters.ageCategory !== 'all') {
        filteredFixtures = filteredFixtures.filter(fixture => fixture.ageCategory === filters.ageCategory);
      }
      
      if (filters.status && filters.status !== 'all') {
        filteredFixtures = filteredFixtures.filter(fixture => fixture.status === filters.status);
      }
      
      return createMockApiResponse(filteredFixtures, filteredFixtures.length);
    }
    
    // TODO: Uncomment when API is ready
    /*
    const response = await secureApi.get<FixturesApiResponse>(
      `/api/v1/fixtures/search`,
      {
        params: { searchTerm, ...filters }
      }
    );
    
    return response.data;
    */
    
    // Fallback to dummy data
    return createMockApiResponse(dummyFixtures, dummyFixtures.length);
    
  } catch (error: any) {
    console.error('❌ Error searching fixtures:', error);
    throw error;
  }
};
